/**
 * Anagrafica Area Manager + collegamento account utente.
 * Un AM vede SOLO i negozi della sua anagrafica: senza account collegato
 * (fail-closed) non vede nulla. Questa card rende il collegamento esplicito.
 * Permesso scrittura: settings.edit_brands.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, UserCheck, UserX } from 'lucide-react';
import { areaManagersApi, usersApi, storesApi, brandsApi } from '../lib/api';
import { useHasPermission } from '../lib/usePermissions';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';

export const AreaManagersEditor = () => {
  const canEdit = useHasPermission('settings.edit_brands');
  const [ams, setAms] = useState([]);
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', brand_id: '', user_id: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, s, b] = await Promise.all([areaManagersApi.list(), storesApi.list(), brandsApi.list()]);
    setAms(a.data || []);
    setStores(s.data || []);
    setBrands(b.data || []);
    try { setUsers((await usersApi.list()).data || []); } catch { setUsers([]); }
  };
  useEffect(() => { load(); }, []);

  const amUsers = useMemo(() => users.filter(u => u.role === 'area_manager'), [users]);
  const usersById = useMemo(() => Object.fromEntries(users.map(u => [u.user_id, u])), [users]);
  const storeCount = (amId) => stores.filter(s =>
    (s.area_manager_ids || []).includes(amId) || s.area_manager_id === amId).length;
  const unlinkedUsers = useMemo(() => {
    const linked = new Set(ams.map(a => a.user_id).filter(Boolean));
    return amUsers.filter(u => !linked.has(u.user_id));
  }, [ams, amUsers]);

  async function linkUser(am, userId) {
    const u = userId ? usersById[userId] : null;
    try {
      await areaManagersApi.update(am.area_manager_id, {
        user_id: u ? u.user_id : null,
        email: u ? u.email : null,
      });
      toast.success(u ? `${am.name} collegato a ${u.email}` : `${am.name} scollegato`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel collegamento');
    }
  }

  async function createAm() {
    if (newForm.name.trim().length < 2) { toast.error('Nome troppo corto'); return; }
    setSaving(true);
    try {
      const u = newForm.user_id ? usersById[newForm.user_id] : null;
      await areaManagersApi.create({
        name: newForm.name.trim(),
        brand_id: newForm.brand_id || '',
        user_id: u ? u.user_id : null,
        email: u ? u.email : null,
      });
      toast.success('Area Manager creato');
      setShowNew(false);
      setNewForm({ name: '', brand_id: '', user_id: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 mb-4" data-testid="am-editor-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-base font-semibold">Area Manager — account collegati</h2>
          <p className="text-xs text-slate-400 mt-1">
            Un Area Manager vede solo i negozi assegnati alla sua anagrafica (colonna Negozi qui sotto).{' '}
            <span className="font-semibold text-slate-600">Senza account collegato o senza negozi assegnati non vede nulla.</span>
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowNew(v => !v)} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="am-new-btn">
            <Plus className="w-4 h-4 mr-2" /> {showNew ? 'Chiudi' : 'Nuovo Area Manager'}
          </Button>
        )}
      </div>

      {showNew && (
        <div className="bg-slate-50 border-b border-slate-200 p-5 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="am-new-form">
          <div>
            <label className="text-xs text-slate-600 font-medium">Nome *</label>
            <Input value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                   placeholder="Es. Mario Rossi" className="rounded-none mt-1" data-testid="am-new-name" />
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">Brand</label>
            <Select value={newForm.brand_id || 'none'} onValueChange={v => setNewForm(f => ({ ...f, brand_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="rounded-none mt-1" data-testid="am-new-brand"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">Account utente</label>
            <Select value={newForm.user_id || 'none'} onValueChange={v => setNewForm(f => ({ ...f, user_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="rounded-none mt-1" data-testid="am-new-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {amUsers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} ({u.email})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={createAm} disabled={saving} className="rounded-none w-full" data-testid="am-new-save">
              {saving ? 'Salvataggio…' : 'Crea'}
            </Button>
          </div>
        </div>
      )}

      {unlinkedUsers.length > 0 && (
        <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-900" data-testid="am-unlinked-users-hint">
          <span className="font-semibold">Utenti con ruolo Area Manager senza anagrafica collegata:</span>{' '}
          {unlinkedUsers.slice(0, 6).map(u => u.email).join(', ')}
          {unlinkedUsers.length > 6 && ` e altri ${unlinkedUsers.length - 6}`} — finché non li colleghi qui sotto, non vedranno alcun negozio.
        </div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-5 py-2.5">Nome</th>
            <th className="px-3 py-2.5">Brand</th>
            <th className="px-3 py-2.5 text-center">Negozi</th>
            <th className="px-3 py-2.5">Account collegato</th>
            <th className="px-5 py-2.5 text-right">Stato</th>
          </tr>
        </thead>
        <tbody>
          {ams.map(am => {
            const linkedUser = am.user_id ? usersById[am.user_id] : null;
            const isLinked = Boolean(am.user_id);
            return (
              <tr key={am.area_manager_id} className="border-b border-slate-100" data-testid={`am-row-${am.area_manager_id}`}>
                <td className="px-5 py-2.5 font-medium text-slate-900">{am.name}</td>
                <td className="px-3 py-2.5 text-slate-500">{brands.find(b => b.brand_id === am.brand_id)?.name || '—'}</td>
                <td className="px-3 py-2.5 text-center">
                  {canEdit ? (
                    <StoresPopover am={am} stores={stores} onSaved={load} />
                  ) : (
                    <span className="text-slate-700">{storeCount(am.area_manager_id)}</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {canEdit ? (
                    <Select value={am.user_id || 'none'} onValueChange={v => linkUser(am, v === 'none' ? null : v)}>
                      <SelectTrigger className="rounded-none h-8 max-w-xs text-xs" data-testid={`am-link-user-${am.area_manager_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuno —</SelectItem>
                        {amUsers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} ({u.email})</SelectItem>)}
                        {am.user_id && !usersById[am.user_id] && (
                          <SelectItem value={am.user_id}>{am.email || am.user_id} (utente non trovato)</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-slate-600 text-xs">{linkedUser?.email || am.email || '—'}</span>
                  )}
                </td>
                <td className="px-5 py-2.5 text-right">
                  {isLinked ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700" data-testid={`am-linked-badge-${am.area_manager_id}`}>
                      <UserCheck className="w-3.5 h-3.5" /> Collegato
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600" data-testid={`am-linked-badge-${am.area_manager_id}`}>
                      <UserX className="w-3.5 h-3.5" /> Non collegato
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
          {ams.length === 0 && (
            <tr><td colSpan={5} className="px-5 py-6 text-center text-sm text-slate-400">Nessuna anagrafica Area Manager. Creane una con "Nuovo Area Manager".</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

function StoresPopover({ am, stores, onSaved }) {
  const amId = am.area_manager_id;
  const assigned = useMemo(() => stores
    .filter(s => (s.area_manager_ids || []).includes(amId) || s.area_manager_id === amId)
    .map(s => s.store_id), [stores, amId]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setSel(new Set(assigned)); setQ(''); }
  }, [open, assigned]);

  const list = useMemo(() => stores
    .filter(s => !s.is_special)
    .filter(s => (s.display_name || s.name || '').toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', 'it')), [stores, q]);

  function toggle(id) {
    setSel(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function save() {
    setSaving(true);
    try {
      await areaManagersApi.setStores(amId, [...sel]);
      toast.success(`${sel.size} negozi assegnati a ${am.name}`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'assegnazione negozi");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
                className={`text-xs font-semibold underline underline-offset-2 ${assigned.length ? 'text-slate-900' : 'text-red-600'}`}
                data-testid={`am-stores-btn-${amId}`}>
          {assigned.length} negozi
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="center">
        <div className="p-2 border-b border-slate-200">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca negozio…"
                 className="h-8 text-sm rounded-none" data-testid={`am-stores-search-${amId}`} />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {list.map(s => (
            <label key={s.store_id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                   data-testid={`am-stores-item-${amId}-${s.store_id}`}>
              <input type="checkbox" checked={sel.has(s.store_id)} onChange={() => toggle(s.store_id)} />
              <span className="truncate">{s.display_name || s.name}</span>
            </label>
          ))}
          {list.length === 0 && <p className="px-3 py-3 text-xs text-slate-400">Nessun negozio trovato.</p>}
        </div>
        <div className="p-2 border-t border-slate-200 flex items-center justify-between">
          <span className="text-[11px] text-slate-500">{sel.size} selezionati</span>
          <Button size="sm" onClick={save} disabled={saving} className="rounded-none h-7 text-xs"
                  data-testid={`am-stores-save-${amId}`}>
            {saving ? 'Salvataggio…' : 'Salva'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
